/**
 * HighlightedResponse — the canonical renderer for a raw engine answer.
 *
 * Moved out of BrandAxisPage 2026-09-25 so the Conversations transcript renders an
 * answer exactly the way Brand Perception does. ONE copy on purpose: two frontend
 * copies of a shared display rule have drifted silently before (BRAND_STRATEGY_LIMITS),
 * and here a drift would mean the same answer highlighted differently on two pages.
 *
 * What it does:
 *   - block markdown: headings, hr, ordered / unordered lists, blockquotes, paragraphs;
 *   - inline markdown: **bold**, *italic*, `code`, [label](url);
 *   - `[n]` citation markers → clickable superscripts into the POSITIONAL `sources`
 *     list (index-aligned, never filtered or re-sorted);
 *   - Cibles highlighting from the persisted `targetHighlights`, each term in its
 *     marque's colour, pair halves gated per occurrence (`isPairSatisfied`);
 *   - the row's own source domains, when named in the prose, in citation violet.
 *
 * `variant="dark"` is the Conversations phone transcript: the same structure on a dark
 * surface. Nothing else differs — only the six chrome colours below.
 *
 * Security: the text and the URLs are attacker-chosen web content. Everything is
 * rendered as React nodes (no dangerouslySetInnerHTML) and every link carries
 * rel="noopener noreferrer".
 */

import { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { TargetHighlight, TargetSourceHighlight } from '@traaker/shared';
import { api } from '../../services/api';
import { bareHost, sourceHref } from '../../utils/sourceUrl';
import {
  isPairSatisfied,
  NO_TARGET_TERMS,
  sourceColorsFrom,
  targetColorHex,
  type TargetTerms,
  targetTermsFrom,
} from '../../utils/targetColors';
import { foldAccentsPreserveLen, termToPattern } from './highlightTerms';

export function extractDomain(url: string) {
  try {
    return url.replace(/^https?:\/\//, '').replace(/\/.*$/, '').toLowerCase();
  } catch {
    return url.toLowerCase();
  }
}

/** The six chrome colours that differ between the light page and the dark phone. */
type Variant = 'light' | 'dark';

const CHROME: Record<Variant, {
  h12: string; h3: string; rule: string; quoteBar: string; quoteInk: string;
  codeBg: string; link: string;
}> = {
  light: {
    h12: 'rgba(11,11,15,0.92)',
    h3: 'rgba(11,11,15,0.72)',
    rule: 'rgba(11,11,15,0.10)',
    quoteBar: 'rgba(11,11,15,0.16)',
    quoteInk: 'rgba(11,11,15,0.72)',
    codeBg: 'rgba(11,11,15,0.06)',
    link: '#8B9DC3',
  },
  dark: {
    h12: '#F2F2F6',
    h3: '#C9C9D2',
    rule: 'rgba(255,255,255,0.14)',
    quoteBar: 'rgba(255,255,255,0.22)',
    quoteInk: '#C9C9D2',
    codeBg: 'rgba(255,255,255,0.10)',
    link: '#8FC6D6',
  },
};

// Render a plain text segment with target + source-domain highlighting.
function renderHighlightedText(
  text: string,
  sourceDomains: string[],
  keyPrefix: string,
  targets: TargetTerms = NO_TARGET_TERMS,
): React.ReactNode[] {
  // Targets FIRST in the alternation: a longer configured term must win over a shorter
  // one it contains, so 'traaker pulse' highlights whole rather than leaving 'Pulse' bare.
  //
  // Brand Strategy's `brandName` + `aliases` used to be a THIRD term list here, painted in
  // the product teal. Dropped 2026-09-23: Cibles is now the authority on what counts as the
  // brand in an answer, and the matcher already returns the brand's own marque among
  // `targetHighlights`. Two sources meant two colours for the same thing — the brand name
  // stayed teal while its marque's configured colour applied to everything else — and the
  // Strategy list bypassed the pair gate, which is what painted a COMPETITOR on Avenue
  // Louise in the client's colour. ⚠ A brand with no marque cible therefore gets no prose
  // highlight at all; that is the intended nudge, not an oversight.
  const allTerms = [...targets.terms, ...sourceDomains];
  if (!text || allTerms.length === 0) return [text];
  // Match against an accent-FOLDED copy of the text (so `previfrance` matches
  // `Prévifrance`), but render slices of the ORIGINAL text so the accents stay
  // on screen. foldAccentsPreserveLen keeps offsets aligned 1:1.
  const folded = foldAccentsPreserveLen(text);
  // ONE capture group per term, so the colour + gate lookups can key on the TERM the
  // matcher gave us rather than on the text we found. Those are no longer the same
  // string: a term's inter-token gap matches whatever punctuation the answer put
  // there, so `jacy'z hotel resort` legitimately matches "Jacy'z Hotel & Resort" —
  // and keying on the matched text would miss every map and silently fall through to
  // the source-domain violet. `termToPattern` emits no groups of its own, which is
  // what keeps group index i+1 aligned with allTerms[i].
  const regex = new RegExp(
    allTerms.map((t) => `(${termToPattern(foldAccentsPreserveLen(t))})`).join('|'),
    'giu',
  );
  const out: React.ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of folded.matchAll(regex)) {
    const start = m.index ?? 0;
    const end = start + m[0].length;
    if (start > last) {
      out.push(<span key={`${keyPrefix}-t-${i++}`}>{text.slice(last, start)}</span>);
    }
    const original = text.slice(start, end); // original text, accents intact
    // Longest-first ordering in `allTerms` still decides which alternative wins; this
    // only asks WHICH one did.
    const hit = allTerms.findIndex((_, k) => m[k + 1] !== undefined);
    const matchedFold = (hit === -1 ? m[0] : allTerms[hit]).toLowerCase();
    // A target gets its marque's colour — that is how two marques under the same
    // ownerId (a sub-brand today, a competitor later) are told apart, the brand's own
    // marque included. A marque with no colour picked yet resolves to the neutral grey.
    //
    // A pair half is painted only where its marque alias sits beside it (2026-09-23).
    // 'louise' on its own is a Brussels avenue, not a branch of the client — and the
    // answer that prompted this named a COMPETITOR on it, which got the brand's colour.
    // Falling through to the source-domain style would be just as wrong, so a failed
    // gate has to leave the word as plain text.
    const gate = targets.gates.get(matchedFold);
    const gated = gate !== undefined && !isPairSatisfied(folded, start, end, gate);
    if (gated) {
      out.push(<span key={`${keyPrefix}-t-${i++}`}>{original}</span>);
      last = end;
      continue;
    }
    const targetHex = targets.colors.get(matchedFold);
    if (targetHex) {
      out.push(
        <Box
          key={`${keyPrefix}-g-${i++}`}
          component="span"
          sx={{
            bgcolor: `${targetHex}26`,
            color: targetHex,
            px: 0.5,
            borderRadius: 0.5,
            fontWeight: 700,
          }}
        >
          {original}
        </Box>,
      );
    } else {
      out.push(
        <Box
          key={`${keyPrefix}-s-${i++}`}
          component="span"
          sx={{
            bgcolor: 'rgba(124, 58, 237, 0.12)',
            color: '#7C3AED',
            px: 0.5,
            borderRadius: 0.5,
            fontWeight: 600,
            textDecoration: 'underline dotted',
          }}
        >
          {original}
        </Box>,
      );
    }
    last = end;
  }
  if (last < text.length) {
    out.push(<span key={`${keyPrefix}-t-${i++}`}>{text.slice(last)}</span>);
  }
  return out;
}

/** One resolvable citation, positionally aligned with the row's `sources`. */
type Citation = {
  href: string | null;
  label: string;
  /** Set when a Cibles target matched this source (D45). The marker wears the
   *  marque's colour instead of the generic citation violet. */
  hex?: string;
};

// Minimal inline markdown → React. Supports **bold**, *italic*, `code`, [text](url),
// and Perplexity-style [n] citation markers (clickable → citations[n-1]).
// Target / source highlighting is applied on plain-text segments.
function renderInlineMarkdown(
  text: string,
  sourceDomains: string[],
  keyPrefix: string,
  citations: Citation[] = [],
  targets: TargetTerms = NO_TARGET_TERMS,
  chrome = CHROME.light,
): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  // Regex with grouped alternation: links, bold **/__, italic */_, inline code,
  // then a trailing [n] citation-marker group (m[8]) — kept LAST so real
  // [label](url) links (m[1]/m[2]) win over a bare [n].
  const re =
    /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|__([^_]+)__|(?<![*\w])\*([^*\n]+)\*(?!\*)|(?<![_\w])_([^_\n]+)_(?!_)|`([^`]+)`|\[(\d{1,3})\]/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let idx = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      out.push(
        ...renderHighlightedText(
          text.slice(last, m.index),
          sourceDomains,
          `${keyPrefix}-p${idx}`,
          targets,
        ),
      );
    }
    if (m[8]) {
      // [n] citation marker → clickable superscript linking to citations[n-1].
      const n = parseInt(m[8], 10);
      const cit = citations[n - 1];
      out.push(
        cit?.href ? (
          <Box
            key={`${keyPrefix}-cit${idx}`}
            component="a"
            href={cit.href}
            target="_blank"
            rel="noopener noreferrer"
            // Always the domain — never the opaque grounding-redirect wrapper.
            title={cit.label}
            sx={{
              display: 'inline-block',
              verticalAlign: 'super',
              fontSize: '0.7em',
              lineHeight: 1,
              px: 0.4,
              ml: '1px',
              borderRadius: 0.5,
              fontWeight: 700,
              color: cit.hex || '#7C3AED',
              bgcolor: cit.hex ? `${cit.hex}26` : 'rgba(124, 58, 237, 0.12)',
              textDecoration: 'none',
              cursor: 'pointer',
            }}
          >
            {n}
          </Box>
        ) : (
          // No matching source → leave the marker as plain text.
          <span key={`${keyPrefix}-cit${idx}`}>{m[0]}</span>
        ),
      );
    } else if (m[1] && m[2]) {
      // [label](url)
      out.push(
        <a
          key={`${keyPrefix}-a${idx}`}
          href={m[2]}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: chrome.link, textDecoration: 'underline' }}
        >
          {renderHighlightedText(m[1], sourceDomains, `${keyPrefix}-a${idx}i`, targets)}
        </a>,
      );
    } else if (m[3] || m[4]) {
      const inner = m[3] || m[4] || '';
      out.push(
        <strong key={`${keyPrefix}-b${idx}`}>
          {renderHighlightedText(inner, sourceDomains, `${keyPrefix}-b${idx}i`, targets)}
        </strong>,
      );
    } else if (m[5] || m[6]) {
      const inner = m[5] || m[6] || '';
      out.push(
        <em key={`${keyPrefix}-i${idx}`}>
          {renderHighlightedText(inner, sourceDomains, `${keyPrefix}-i${idx}i`, targets)}
        </em>,
      );
    } else if (m[7]) {
      out.push(
        <Box
          key={`${keyPrefix}-c${idx}`}
          component="code"
          sx={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: '0.85em',
            bgcolor: chrome.codeBg,
            px: 0.5,
            py: 0.2,
            borderRadius: 0.5,
          }}
        >
          {m[7]}
        </Box>,
      );
    }
    last = m.index + m[0].length;
    idx++;
  }
  if (last < text.length) {
    out.push(
      ...renderHighlightedText(
        text.slice(last),
        sourceDomains,
        `${keyPrefix}-p${idx}`,
        targets,
      ),
    );
  }
  return out;
}

/**
 * marque targetId → colour token, fetched once per brand and shared by every
 * response panel on the page (a narrative audit renders dozens).
 *
 * D45: the row carries `rootMarqueId`, never a colour — the consumer resolves it,
 * so recolouring a marque doesn't need a re-audit. A failure resolves to no
 * colours, which degrades to the plain brand highlight rather than to no highlight.
 */
export type MarqueColors = {
  /** targetId → colour token (for targetTermsFrom / sourceColorsFrom). */
  colors: Record<string, string>;
  /**
   * bareHost(domain) → hex, for brand-domain source chips not caught by a
   * registered Cibles target (e.g. a blog post on dessange.com that isn't a
   * contenu target).  Keyed by bareHost so subdomain/sibling-TLD fallback goes
   * through the isBrandOwnedHost loop at lookup time.
   */
  domainHexes: Record<string, string>;
};

export const EMPTY_MARQUE_COLORS: MarqueColors = { colors: {}, domainHexes: {} };

const marqueColorCache = new Map<string, Promise<MarqueColors>>();

export function useMarqueColors(brandId?: string): MarqueColors {
  const [result, setResult] = useState<MarqueColors>(EMPTY_MARQUE_COLORS);
  useEffect(() => {
    if (!brandId) return;
    let live = true;
    let pending = marqueColorCache.get(brandId);
    if (!pending) {
      // Marques only — the colour lives on the marque, and there are few of them.
      // We also capture domain fields so brand-domain source chips that aren't a
      // registered Cibles target still show the marque's configured colour.
      pending = api
        .get(`/brands/${brandId}/targets?category=marque&limit=200`)
        .then((d: any) => {
          const colors: Record<string, string> = {};
          const domainHexes: Record<string, string> = {};
          const palette = ['blue','indigo','violet','purple','fuchsia','pink','rose','red','orange','amber','yellow','lime','green','emerald','teal','cyan','sky'];
          for (const it of (d?.items || []) as { targetId: string; color?: string | null; fields?: Array<{ name: string; value: string }> }[]) {
            let token: string;
            if (it.color) {
              token = it.color;
            } else {
              let h = 0;
              for (let i = 0; i < it.targetId.length; i++) h = (h * 31 + it.targetId.charCodeAt(i)) >>> 0;
              token = palette[h % palette.length];
            }
            colors[it.targetId] = token;
            for (const f of it.fields || []) {
              if (f.name === 'domain' && f.value) {
                const key = bareHost(f.value);
                if (key) domainHexes[key] = targetColorHex(token);
              }
            }
          }
          return { colors, domainHexes };
        })
        .catch(() => EMPTY_MARQUE_COLORS);
      marqueColorCache.set(brandId, pending);
    }
    pending.then((m) => { if (live) setResult(m); });
    return () => { live = false; };
  }, [brandId]);
  return result;
}

/** `|---|:--:|---:|` — a GFM table's header separator (pipes optional at the ends). */
const TABLE_SEPARATOR = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/;

function isTableRow(line: string): boolean {
  return /^\s*\|.*\|\s*$/.test(line);
}

/** A table row's cells: split on unescaped `|`, the empty edge cells dropped. */
function tableCells(line: string): string[] {
  const cells = line.trim().split(/(?<!\\)\|/).map((c) => c.trim().replace(/\\\|/g, '|'));
  if (cells[0] === '') cells.shift();
  if (cells[cells.length - 1] === '') cells.pop();
  return cells;
}

export function HighlightedResponse({
  text,
  brandId,
  sources,
  sourceLinks,
  targetHighlights,
  targetSourceHighlights,
  variant = 'light',
}: {
  text: string;
  brandId?: string;
  sources?: string[];
  /** Positionally aligned with `sources`; Gemini-only (see the source chips). */
  sourceLinks?: string[];
  /**
   * Cibles terms matched in this answer's BODY, persisted at audit time — the ONLY
   * source of prose highlighting since 2026-09-23, the brand's own name included.
   */
  targetHighlights?: TargetHighlight[];
  /** Cibles targets matched in this answer's CITATIONS — cited but not named, so
   *  they colour the `[n]` marker and the source chip rather than the prose. */
  targetSourceHighlights?: TargetSourceHighlight[];
  /** `'dark'` = the Conversations phone transcript. */
  variant?: Variant;
}) {
  const { t } = useTranslation();
  const { colors: marqueColors } = useMarqueColors(brandId);
  const chrome = CHROME[variant];

  const targetTerms = useMemo<TargetTerms>(
    () => targetTermsFrom(targetHighlights, marqueColors),
    [targetHighlights, marqueColors],
  );

  const sourceDomains = useMemo(() => {
    const set = new Set<string>();
    (sources || []).forEach((url) => {
      const d = extractDomain(url);
      if (d && d.length >= 4) set.add(d);
    });
    return Array.from(set).sort((a, b) => b.length - a.length);
  }, [sources]);

  // `[n]` markers are 1-based into `sources`; the href comes from the aligned
  // `sourceLinks` when present (Gemini's grounding-redirect wrapper, which the
  // browser follows on click), else from `sources[i]` directly. The label is
  // ALWAYS the domain — a wrapper URL must never be shown.
  const sourceColors = useMemo(
    () => sourceColorsFrom(targetSourceHighlights, marqueColors),
    [targetSourceHighlights, marqueColors],
  );

  const citations = useMemo<Citation[]>(
    () =>
      (sources || []).map((s, i) => ({
        href: sourceHref(sourceLinks?.[i] || s),
        label: extractDomain(s),
        hex: sourceColors.get(i),
      })),
    [sources, sourceLinks, sourceColors],
  );

  if (!text) {
    return (
      <Typography variant="body2" color={variant === 'dark' ? chrome.h3 : 'text.secondary'}>
        {t('axisPage.noResponse')}
      </Typography>
    );
  }

  // Block-level markdown parser. Line-based: headings, horizontal rules,
  // unordered / ordered lists, blockquotes, GFM pipe tables, blank-line paragraph breaks.
  //
  // Engine responses (esp. Perplexity) sometimes glue a heading onto the end
  // of a previous paragraph without a newline, e.g.
  //   "... réachats. #### 2. **Programmes de fidélité**"
  // The heading regex below requires ^, so we pre-split any inline "####"
  // run onto its own line. Same for inline horizontal rules ("--- foo").
  const normalized = text
    .replace(/\r\n/g, '\n')
    .replace(/([^\n])\s+(#{1,6}\s)/g, '$1\n\n$2');
  const lines = normalized.split('\n');
  const blocks: React.ReactNode[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const joined = paragraph.join(' ');
    blocks.push(
      <Typography
        key={`p-${blocks.length}`}
        variant="body2"
        sx={{ mb: 1, lineHeight: 1.65 }}
      >
        {renderInlineMarkdown(joined, sourceDomains, `p${blocks.length}`, citations, targetTerms, chrome)}
      </Typography>,
    );
    paragraph = [];
  };

  const flushList = () => {
    if (!list) return;
    const { ordered, items } = list;
    blocks.push(
      <Box
        key={`l-${blocks.length}`}
        component={ordered ? 'ol' : 'ul'}
        sx={{ pl: 3, mb: 1, mt: 0 }}
      >
        {items.map((it, i) => (
          <Box component="li" key={i} sx={{ mb: 0.25 }}>
            <Typography variant="body2" component="span" sx={{ lineHeight: 1.65 }}>
              {renderInlineMarkdown(it, sourceDomains, `li${blocks.length}-${i}`, citations, targetTerms, chrome)}
            </Typography>
          </Box>
        ))}
      </Box>,
    );
    list = null;
  };

  const flushAll = () => {
    flushParagraph();
    flushList();
  };

  const headingSx = (level: number) => ({
    mt: blocks.length === 0 ? 0 : 1.5,
    mb: 0.75,
    fontWeight: 700,
    fontSize:
      level === 1 ? '1.15rem' : level === 2 ? '1.05rem' : level === 3 ? '0.98rem' : '0.92rem',
    color: level <= 2 ? chrome.h12 : chrome.h3,
  });

  for (let li = 0; li < lines.length; li++) {
    const rawLine = lines[li];
    const line = rawLine.replace(/\s+$/g, '');

    // Blank line → flush buffers
    if (!line.trim()) {
      flushAll();
      continue;
    }

    // GFM pipe table: a `| a | b |` row directly followed by a `|---|:--:|` separator
    // (2026-11-02 — conversation turns store the raw answer; BP flattens tables at write
    // time, `orchestrator._rewrite_pipe_tables`). Cells keep inline markdown, highlights
    // and `[n]` markers.
    if (isTableRow(line) && li + 1 < lines.length && TABLE_SEPARATOR.test(lines[li + 1])) {
      flushAll();
      const header = tableCells(line);
      const align = tableCells(lines[li + 1]).map((c) => (/^:-+:$/.test(c) ? 'center' : /-+:$/.test(c) ? 'right' : 'left'));
      const rows: string[][] = [];
      li += 1;
      while (li + 1 < lines.length && isTableRow(lines[li + 1])) {
        li += 1;
        rows.push(tableCells(lines[li]));
      }
      const k = blocks.length;
      const cellSx = { border: `1px solid ${chrome.rule}`, px: 1, py: 0.75, verticalAlign: 'top', fontSize: '0.8125rem', lineHeight: 1.5 };
      blocks.push(
        <Box key={`t-${k}`} sx={{ overflowX: 'auto', mb: 1.25, maxWidth: '100%' }}>
          <Box component="table" sx={{ borderCollapse: 'collapse', minWidth: '100%' }}>
            <Box component="thead">
              <Box component="tr">
                {header.map((c, ci) => (
                  <Box component="th" key={ci} sx={{ ...cellSx, fontWeight: 700, textAlign: align[ci] ?? 'left' }}>
                    {renderInlineMarkdown(c, sourceDomains, `t${k}h${ci}`, citations, targetTerms, chrome)}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {rows.map((r, ri) => (
                <Box component="tr" key={ri}>
                  {header.map((_, ci) => (
                    <Box component="td" key={ci} sx={{ ...cellSx, textAlign: align[ci] ?? 'left' }}>
                      {renderInlineMarkdown(r[ci] ?? '', sourceDomains, `t${k}r${ri}c${ci}`, citations, targetTerms, chrome)}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>,
      );
      continue;
    }

    // Stray lone marker line with no content (engine artifact: ".", "-",
    // "**", "*", "__") → skip. HR (3+ of -/*/_) is handled below.
    if (/^\s*([-*+.]|\*{1,2}|_{1,2})\s*$/.test(line)) {
      flushAll();
      continue;
    }

    // Horizontal rule
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      flushAll();
      blocks.push(
        <Box
          key={`hr-${blocks.length}`}
          sx={{ borderBottom: `1px solid ${chrome.rule}`, my: 1.25 }}
        />,
      );
      continue;
    }

    // Heading
    const headingMatch = /^(#{1,6})\s+(.+)$/.exec(line);
    if (headingMatch) {
      flushAll();
      const level = headingMatch[1].length;
      const content = headingMatch[2].replace(/\s+#+\s*$/, '');
      blocks.push(
        <Typography
          key={`h-${blocks.length}`}
          variant="body1"
          sx={headingSx(level)}
        >
          {renderInlineMarkdown(content, sourceDomains, `h${blocks.length}`, citations, targetTerms, chrome)}
        </Typography>,
      );
      continue;
    }

    // Blockquote
    const bqMatch = /^\s{0,3}>\s?(.*)$/.exec(line);
    if (bqMatch) {
      flushAll();
      blocks.push(
        <Box
          key={`bq-${blocks.length}`}
          sx={{
            borderLeft: `3px solid ${chrome.quoteBar}`,
            pl: 1.5,
            color: chrome.quoteInk,
            fontStyle: 'italic',
            mb: 1,
          }}
        >
          <Typography variant="body2">
            {renderInlineMarkdown(bqMatch[1], sourceDomains, `bq${blocks.length}`, citations, targetTerms, chrome)}
          </Typography>
        </Box>,
      );
      continue;
    }

    // Unordered list item
    const ulMatch = /^\s*[-*+]\s+(.+)$/.exec(line);
    if (ulMatch) {
      flushParagraph();
      if (!list || list.ordered) {
        flushList();
        list = { ordered: false, items: [] };
      }
      list.items.push(ulMatch[1]);
      continue;
    }

    // Ordered list item
    const olMatch = /^\s*\d+[.)]\s+(.+)$/.exec(line);
    if (olMatch) {
      flushParagraph();
      if (!list || !list.ordered) {
        flushList();
        list = { ordered: true, items: [] };
      }
      list.items.push(olMatch[1]);
      continue;
    }

    // Continuation of list item (indented)
    if (list && /^\s{2,}\S/.test(rawLine)) {
      list.items[list.items.length - 1] += ' ' + line.trim();
      continue;
    }

    // Fallback: paragraph text
    flushList();
    paragraph.push(line.trim());
  }

  flushAll();

  return <Box>{blocks}</Box>;
}
